import React, { useEffect, useState } from 'react';
import {
  ListProps,
  TextField,
  ReferenceField,
  RowClickFunction,
  Identifier,
  useRedirect,
  RaRecord,
  useNotify,
} from 'react-admin';

import {
  PhoneField,
  TextInput,
  Grid,
  GridList,
  Forbidden,
  BooleanField,
  SelectInput,
  ReferenceInput,
  SelectArrayInput,
  DateField,
  ButtonLinkField,
} from '@components';
import { useExporter, useRbacList } from '@hooks';
import { adminSettingsHttp, cropHttp, stateHttp } from '@network';
import { axiosErrorToString } from '@utils';
import cls from './LeadList.module.css';
import { AdminOptionCats } from '@types';

export const LeadList = (props: ListProps) => {
  const exporter = useExporter({
    filename: 'Verqor Leads',
    props: {
      'id': {
        label: 'ID',
      },
      'name': {
        label: 'Name',
      },
      'phone': {
        label: 'Phone',
        transform: 'phone',
      },
      'email': {
        label: 'Email',
      },
      'stageName': {
        label: 'Stage',
      },
      'stateId': {
        label: 'State',
        prop: 'state.name',
      },
      'activationName': {
        label: 'Activation',
      },
      'responsibleAdmin.name': {
        label: 'Manager',
        prop: 'responsibleAdmin.name',
      },
      'cropNames': {
        label: 'Crops',
      },
      'sfLink': {
        label: 'SalesForce',
        prop: 'sfLink',
      },
      'archived': {
        label: 'Archived',
        transform: 'boolean',
      },
      'createdAt': {
        label: 'Date of creation',
        transform: 'date',
      },
      'updatedAt': {
        label: 'Last modification',
        transform: 'date',
      },
    },
  });

  const rbacList = useRbacList();
  const redirect = useRedirect();
  const notify = useNotify();

  const [cropOptions, setCropOptions] = useState<any[]>([]);
  const [stateOptions, setStateOptions] = useState<any[]>([]);
  const [stageOptions, setStageOptions] = useState<any[]>([]);

  useEffect(() => {
    (async () => {
      try {
        const crops = cropHttp.getAll();
        const states = stateHttp.getAll();
        const stages = adminSettingsHttp
          .getOptionsByCategory(AdminOptionCats.SALES_LEADS_STAGE);
        const [loadedCrops, loadedStates, loadedStages] = await Promise
          .all([crops, states, stages]);

        setCropOptions(loadedCrops
          .sort((a, b) => a.nameMx.localeCompare(b.nameMx))
          .map(crop => ({
            id: crop.id,
            name: crop.nameEn || crop.nameMx || `Crop id: ${crop.id}`,
          })));
        setStateOptions(loadedStates
          .sort((a, b) => a.name.localeCompare(b.name))
          .map(state => ({
            id: state.id,
            name: state.name || `State id: ${state.id}`,
          })),
        );
        setStageOptions(loadedStages
          .sort((a, b) => a.name.localeCompare(b.name))
          .map(o => ({
            id: o.id,
            name: o.name.slice(0, 1).toUpperCase() + o.name.slice(1),
          })),
        );
      } catch (e) {
        notify(axiosErrorToString(e));
      }
    })();
  }, []);

  const userRowClick: RowClickFunction = (id: Identifier, resource: string, record: RaRecord) => {
    redirect(`/${resource}/${id}`);
    return false;
  };

  if (!rbacList) {
    return <Forbidden />;
  }

  return (
    <GridList
      {...props}
      exporter={exporter}
      filters={[
        ...Filters,
        <SelectArrayInput
          key="cropIds"
          source="cropIds"
          choices={cropOptions}
          className="MS-field"
          label="Crops"
          style={{ width: 268 }}
        />,
        <SelectArrayInput
          key="stateIds"
          source="stateIds"
          choices={stateOptions}
          className="MS-field"
          label="State"
          style={{ width: 268 }}
        />,
        <SelectInput
          key="stage"
          source="stage"
          choices={stageOptions}
        />,
      ]}
      sort={{ field: 'id', order: 'DESC' }}
      className={cls.gridList}
    >
      <Grid
        rowClick={userRowClick}
        defaultColumns={'id name phone email stageName activationName hubspotLink createdAt'.split(' ')}
      >
        <TextField source="id" label="ID" />
        <TextField source="name" label="Name" />
        <PhoneField source="phone" label="Phone" />
        <TextField source="email" label="Email" />
        <TextField source="stageName" label="Stage" />
        <ReferenceField reference="user" source="addedByUserId" label="Added by User Id">
          <TextField source="fullName" />
        </ReferenceField>
        <TextField source="state.name" label="State" />
        <TextField source="activationName" label="Activation" />
        <TextField source="responsibleAdmin.name" label="Manager" />
        <TextField source="cropNames" label="Crops" />
        <ButtonLinkField source="sfLink" text="SalesForce" label="SalesForce" center isBlue />
        <BooleanField source="archived" label="Archived" />
        <DateField source="createdAt" label="Date of creation" />
        <DateField source="updatedAt" label="Last Modification" />
      </Grid>
    </GridList>
  );
};

const Filters = [
  <TextInput source="q" key="q" variant="outlined" label="Search" alwaysOn />,
  <ReferenceInput
    key="responsibleAdminId"
    source="responsibleAdminId"
    reference="admin"
    label="Manager"
    allowEmpty
    perPage={1000}
    sort={{ field: 'name', order: 'ASC' }}
  >
    <SelectInput optionText="name" label="Manager" style={{ minWidth: 180 }} />
  </ReferenceInput>,
];
